import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  Search, 
  DollarSign,
  Loader2,
  MoreVertical,
  Server,
  Mail,
  Globe,
  Wrench,
  Package,
  Pencil,
  Trash2,
  TrendingUp,
  Building2,
  Calendar
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryOptions = [
  { value: 'hosting', label: 'Hosting', icon: Server },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'domain', label: 'Domain', icon: Globe },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench },
  { value: 'other', label: 'Other', icon: Package }
];

const cycleOptions = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

const Billing = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [overview, setOverview] = useState({ tenants: [], grand_total_monthly: 0 });
  const [tenants, setTenants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    tenant_id: '',
    product_id: '',
    name: '',
    description: '',
    category: 'hosting',
    amount: '',
    billing_cycle: 'monthly',
    project_id: ''
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchData();
    }
  }, [isSuperAdmin]);

  const fetchData = async () => {
    try {
      const [overviewRes, tenantsRes, projectsRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/billing-overview`, getAuthHeaders()),
        axios.get(`${API_URL}/tenants`, getAuthHeaders()),
        axios.get(`${API_URL}/projects`, getAuthHeaders()),
        axios.get(`${API_URL}/products-services?active_only=true`, getAuthHeaders())
      ]);
      setOverview(overviewRes.data);
      setTenants(tenantsRes.data);
      setProjects(projectsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (productId) => {
    if (productId === 'custom') {
      setFormData({ 
        ...formData, 
        product_id: 'custom',
        name: '',
        description: '',
        category: 'hosting',
        amount: '',
        billing_cycle: 'monthly'
      });
    } else {
      const product = products.find(p => p.id === productId);
      if (product) {
        setFormData({
          ...formData,
          product_id: productId,
          name: product.name,
          description: product.description || '',
          category: product.category,
          amount: product.default_amount.toString(),
          billing_cycle: product.default_billing_cycle
        });
      }
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/billing-items`, {
        tenant_id: formData.tenant_id,
        name: formData.name,
        description: formData.description,
        category: formData.category,
        amount: parseFloat(formData.amount),
        billing_cycle: formData.billing_cycle,
        project_id: formData.project_id === 'none' ? null : (formData.project_id || null)
      }, getAuthHeaders());
      toast.success('Billing item created');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create billing item');
    }
  };

  const resetForm = () => {
    setFormData({
      tenant_id: '',
      product_id: '',
      name: '',
      description: '',
      category: 'hosting',
      amount: '',
      billing_cycle: 'monthly',
      project_id: ''
    });
  };

  const filteredTenants = overview.tenants.filter(t => 
    t.tenant_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryIcon = (category) => {
    const cat = categoryOptions.find(c => c.value === category);
    return cat ? cat.icon : Package;
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Only super admin can access billing</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Recurring Billing
          </h1>
          <p className="text-slate-500 mt-1">Manage recurring charges for all tenants</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-billing-item-btn">
              <Plus size={18} className="mr-2" />
              Add Billing Item
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Add Billing Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Tenant *</Label>
                <Select
                  value={formData.tenant_id}
                  onValueChange={(value) => setFormData({ ...formData, tenant_id: value, project_id: '' })}
                >
                  <SelectTrigger data-testid="billing-tenant-select">
                    <SelectValue placeholder="Select tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map(tenant => (
                      <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Product/Service</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={handleProductSelect}
                >
                  <SelectTrigger data-testid="billing-product-select">
                    <SelectValue placeholder="Select from catalog or custom" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom Item</SelectItem>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} - ${product.default_amount}/{product.default_billing_cycle === 'monthly' ? 'mo' : product.default_billing_cycle === 'quarterly' ? 'qtr' : 'yr'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Select a predefined service or create a custom item</p>
              </div>

              <div className="space-y-2">
                <Label>Item Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Website Hosting, Business Email"
                  required
                  data-testid="billing-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="billing-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Billing Cycle *</Label>
                  <Select
                    value={formData.billing_cycle}
                    onValueChange={(value) => setFormData({ ...formData, billing_cycle: value })}
                  >
                    <SelectTrigger data-testid="billing-cycle-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cycleOptions.map(cycle => (
                        <SelectItem key={cycle.value} value={cycle.value}>{cycle.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount ($) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="billing-amount-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Project (Optional)</Label>
                  <Select
                    value={formData.project_id}
                    onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                  >
                    <SelectTrigger data-testid="billing-project-select">
                      <SelectValue placeholder="Link to project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No project</SelectItem>
                      {projects
                        .filter(p => p.organization_id === formData.tenant_id)
                        .map(project => (
                          <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-billing-item-btn">
                  Add Item
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Grand Total */}
      <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-slate-900 to-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center">
                <DollarSign size={28} className="text-white" />
              </div>
              <div>
                <p className="text-slate-300 text-sm">Total Monthly Recurring Revenue</p>
                <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  ${overview.grand_total_monthly.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-slate-300 text-sm">Active Tenants</p>
              <p className="text-2xl font-bold text-white">{overview.tenants.filter(t => t.item_count > 0).length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search tenants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-billing-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tenants Billing Grid */}
      {filteredTenants.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <DollarSign size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No billing data yet</h3>
            <p className="text-slate-500 mt-1">Add billing items to start tracking recurring revenue</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTenants.map((tenant) => (
            <Card 
              key={tenant.tenant_id} 
              className="border-slate-200 shadow-sm card-hover cursor-pointer"
              onClick={() => navigate(`/billing/${tenant.tenant_id}`)}
              data-testid={`billing-tenant-card-${tenant.tenant_id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Building2 size={20} className="text-slate-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-slate-900">
                      {tenant.tenant_name}
                    </CardTitle>
                    <p className="text-xs text-slate-500">{tenant.item_count} billing items</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Monthly Total</p>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                      ${tenant.monthly_total.toLocaleString()}
                    </p>
                  </div>
                  {tenant.monthly_total > 0 && (
                    <div className="flex items-center gap-1 text-emerald-600 text-sm">
                      <TrendingUp size={16} />
                      <span>Active</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Billing;
